import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  TextField,
  IconButton,
  Chip,
  Alert,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack, Notifications, Language, Palette, Security, CloudUpload, ChevronRight, VpnKey, Add, Delete, ContentCopy, Check } from '@mui/icons-material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useColorMode } from '../context/ColorModeContext';
import { useTranslation } from 'react-i18next';
import { authService, notificationService, apiTokenService } from '../services/api';

function SettingsPage() {
  const navigate = useNavigate();
  const { mode, toggleColorMode } = useColorMode();
  const { i18n, t } = useTranslation();
  const { user, setUser } = useOutletContext();
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [loadingPrefs, setLoadingPrefs] = useState(true);

  // API Tokens state
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [tokenDialog, setTokenDialog] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [creatingToken, setCreatingToken] = useState(false);
  const [newTokenValue, setNewTokenValue] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  useEffect(() => {
    notificationService.getEmailPreferences()
      .then(res => setEmailPrefs(res.data.data))
      .catch(() => {})
      .finally(() => setLoadingPrefs(false));
  }, []);

  useEffect(() => {
    apiTokenService.list()
      .then(res => setTokens(res.data.tokens))
      .catch(() => {})
      .finally(() => setLoadingTokens(false));
  }, []);

  const handleEmailToggle = async () => {
    if (!emailPrefs) return;
    const updated = { email_enabled: !emailPrefs.email_enabled };
    setEmailPrefs(prev => ({ ...prev, ...updated }));
    try {
      const res = await notificationService.updateEmailPreferences(updated);
      setEmailPrefs(res.data.data);
    } catch {
      setEmailPrefs(prev => ({ ...prev, email_enabled: !updated.email_enabled }));
    }
  };

  const BACKGROUND_PRESETS = [
    '', // Default (none)
    'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1920&q=80', // Nature
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1920&q=80', // City
    'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?auto=format&fit=crop&w=1920&q=80', // Mountain
    'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1920&q=80', // Tech
  ];

  const handleBackgroundChange = async (url) => {
    const updatedUser = { ...user, dashboard_background: url };
    setUser(updatedUser);
    localStorage.setItem('era_user', JSON.stringify(updatedUser));
    try {
      await authService.updateProfile({ dashboard_background: url });
    } catch (err) {
      console.error(err);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleBackgroundChange(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const handleCreateToken = async () => {
    if (!tokenName.trim()) return;
    setCreatingToken(true);
    try {
      const res = await apiTokenService.create({ name: tokenName.trim() });
      setNewTokenValue(res.data.token);
      setTokens(prev => [res.data.record, ...prev]);
      setTokenName('');
    } catch (err) {
      console.error(err);
    } finally {
      setCreatingToken(false);
    }
  };

  const handleRevokeToken = async (id) => {
    setRevokingId(id);
    try {
      await apiTokenService.revoke(id);
      setTokens(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error(err);
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(newTokenValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCloseTokenDialog = () => {
    setTokenDialog(false);
    setNewTokenValue(null);
    setTokenName('');
    setCopied(false);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button 
        startIcon={<ArrowBack />} 
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 2 }}
      >
        {t('settingsPage.back')}
      </Button>

      <Typography variant="h4" gutterBottom fontWeight="bold">
        {t('settingsPage.title')}
      </Typography>

      <Paper elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: '8px 8px 0 0' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Palette /> {t('settingsPage.appearanceTitle')}
          </Typography>
        </Box>
        <Divider />
        <List>
          <ListItem>
            <ListItemText 
              primary={t('settingsPage.darkModeTitle')}
              secondary={t('settingsPage.darkModeSubtitle')}
            />
            <ListItemSecondaryAction>
              <Switch 
                edge="end" 
                checked={mode === 'dark'}
                onChange={toggleColorMode}
              />
            </ListItemSecondaryAction>
          </ListItem>
          
          <Divider variant="inset" component="li" />
          
          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
            <Box sx={{ width: '100%', mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>{t('settingsPage.backgroundTitle')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settingsPage.backgroundSubtitle')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: '100%' }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ height: 80, width: 120, display: 'flex', flexDirection: 'column', gap: 1 }}
              >
                {t('settingsPage.backgroundImport')}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileUpload}
                />
              </Button>
              
              {BACKGROUND_PRESETS.map((url, index) => (
                <Box
                  key={index}
                  onClick={() => handleBackgroundChange(url)}
                  sx={{
                    width: 120,
                    height: 80,
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: 'grey.200',
                    backgroundImage: url ? `url(${url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: user?.dashboard_background === url ? '2px solid primary.main' : '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { opacity: 0.8 }
                  }}
                >
                  {!url && <Typography variant="caption" color="text.secondary">{t('settingsPage.backgroundDefault')}</Typography>}
                </Box>
              ))}
            </Box>
          </ListItem>
        </List>
      </Paper>

      <Paper elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: '8px 8px 0 0' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Language /> {t('settingsPage.languageRegionTitle')}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ p: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="language-select-label">{t('settingsPage.languageLabel')}</InputLabel>
            <Select
              labelId="language-select-label"
              value={i18n.language.split('-')[0]}
              label={t('settingsPage.languageLabel')}
              onChange={handleLanguageChange}
            >
              <MenuItem value="fr">{t('settingsPage.languageFrench')}</MenuItem>
              <MenuItem value="en">{t('settingsPage.languageEnglish')}</MenuItem>
              <MenuItem value="ar">{t('settingsPage.languageArabic')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: '8px 8px 0 0' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Notifications /> {t('settingsPage.notificationsTitle')}
          </Typography>
        </Box>
        <Divider />
        <List>
          <ListItem>
            <ListItemText
              primary={t('settingsPage.emailNotificationsTitle')}
              secondary={t('settingsPage.emailNotificationsSubtitle')}
            />
            <ListItemSecondaryAction>
              {loadingPrefs
                ? <Skeleton variant="rectangular" width={50} height={24} sx={{ borderRadius: 12 }} />
                : <Switch
                    edge="end"
                    checked={!!emailPrefs?.email_enabled}
                    onChange={handleEmailToggle}
                  />
              }
            </ListItemSecondaryAction>
          </ListItem>
          <Divider variant="inset" component="li" />
          <ListItem
            button
            onClick={() => navigate('/notifications/preferences')}
            sx={{ cursor: 'pointer' }}
          >
            <ListItemText
              primary="Gérer les types de notifications email"
              secondary="Choisir quels emails vous recevez (tâches, réunions, projets…)"
            />
            <ChevronRight color="action" />
          </ListItem>
        </List>
      </Paper>

      <Paper elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: '8px 8px 0 0' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security /> {t('settingsPage.securityTitle')}
          </Typography>
        </Box>
        <Divider />
        <List>
          <ListItem button>
            <ListItemText
              primary={t('settingsPage.twoFactorTitle')}
              secondary={t('settingsPage.twoFactorSubtitle')}
            />
            <Button variant="outlined" size="small">{t('settingsPage.configure')}</Button>
          </ListItem>
        </List>
      </Paper>

      {/* API Tokens */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKey /> {t('settingsPage.apiTokensTitle')}
          </Typography>
          <Button startIcon={<Add />} variant="contained" size="small" onClick={() => setTokenDialog(true)}>
            {t('settingsPage.apiTokensNew')}
          </Button>
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('settingsPage.apiTokensSubtitle')}
          </Typography>
          {loadingTokens ? (
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
          ) : tokens.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              {t('settingsPage.apiTokensEmpty')}
            </Typography>
          ) : (
            <List disablePadding>
              {tokens.map((token, i) => (
                <React.Fragment key={token.id}>
                  {i > 0 && <Divider />}
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2">{token.name}</Typography>
                          <Chip label={token.token_prefix + '…'} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {t('settingsPage.apiTokensCreatedAt')} {new Date(token.created_at).toLocaleDateString()}
                          {' · '}
                          {token.last_used_at
                            ? `${t('settingsPage.apiTokensLastUsed')} ${new Date(token.last_used_at).toLocaleDateString()}`
                            : t('settingsPage.apiTokensNeverUsed')}
                        </Typography>
                      }
                    />
                    <Tooltip title={t('settingsPage.apiTokensRevoke')}>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRevokeToken(token.id)}
                        disabled={revokingId === token.id}
                      >
                        {revokingId === token.id
                          ? <CircularProgress size={16} />
                          : <Delete fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {/* Dialog création de token */}
      <Dialog open={tokenDialog} onClose={!creatingToken ? handleCloseTokenDialog : undefined} maxWidth="sm" fullWidth>
        <DialogTitle>{t('settingsPage.apiTokensNew')}</DialogTitle>
        <DialogContent>
          {newTokenValue ? (
            <Box sx={{ mt: 1 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('settingsPage.apiTokensWarning')}
              </Alert>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', bgcolor: 'grey.100', p: 1.5, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all', fontSize: 13 }}>
                  {newTokenValue}
                </Typography>
                <Tooltip title={copied ? t('settingsPage.apiTokensCopied') : t('settingsPage.apiTokensCopy')}>
                  <IconButton size="small" onClick={handleCopyToken}>
                    {copied ? <Check color="success" /> : <ContentCopy fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ) : (
            <TextField
              autoFocus
              fullWidth
              label={t('settingsPage.apiTokensNameLabel')}
              placeholder={t('settingsPage.apiTokensNamePlaceholder')}
              value={tokenName}
              onChange={e => setTokenName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateToken()}
              sx={{ mt: 1 }}
              disabled={creatingToken}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTokenDialog} disabled={creatingToken}>
            {newTokenValue ? t('settingsPage.apiTokensClose') : t('settingsPage.apiTokensCancel')}
          </Button>
          {!newTokenValue && (
            <Button
              variant="contained"
              onClick={handleCreateToken}
              disabled={!tokenName.trim() || creatingToken}
              startIcon={creatingToken ? <CircularProgress size={16} color="inherit" /> : <Add />}
            >
              {t('settingsPage.apiTokensCreate')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Container>
  );
}

export default SettingsPage;
